import { useMemo } from "react";
import useSWR from "swr";
import { NormalizedSubscription, TierLabel } from "@memento/types/subscription";
import { buildNoneSubscription } from "@memento/library/subscription";
import {
  getLoadingStatus,
  useAuthenticatedFetcher,
  LoadingStatus,
} from "src/libs/fetcher";
import { useAuth } from "src/hooks/useAuth";
import { getSubscriptionEndpointUrl } from "src/libs/api-endpoint-lib";

export const useSubscription = () => {
  const fetcher = useAuthenticatedFetcher();
  const { isLoggedIn } = useAuth();

  // don't run the fetch if we aren't logged in
  const key = isLoggedIn ? getSubscriptionEndpointUrl() : null;

  const { data, error, mutate } = useSWR<NormalizedSubscription, Error>(
    key,
    (url: string) => fetcher(url)
  );

  const noneSubscription = useMemo(() => {
    if (isLoggedIn) {
      return null;
    } else {
      return buildNoneSubscription(null);
    }
  }, [isLoggedIn]);

  // If the user isn't logged in, then just return a NONE subscription. Ideally
  // the server would return that if we aren't logged in. However, it's not
  // simple to allow an endpoint to work whether a user is logged in or not,
  // and also to provide the user's if they are logged in.
  if (!isLoggedIn) {
    return {
      subscription: noneSubscription,
      hasActiveSubscription: false,
      error: undefined,
      status: LoadingStatus.SUCCESS,
      mutate,
    };
  } else {
    const subscription = data || null;

    const hasActiveSubscription = Boolean(
      subscription?.tier.label &&
      subscription?.tier.label !== TierLabel.NONE &&
      subscription?.isActive
    );

    return {
      subscription,
      hasActiveSubscription,
      error,
      status: getLoadingStatus(key, data, error),
      mutate,
    };
  }
};
